<template>
    <div class="product-list-page" v-if="userHasProductAdmin">
        <main-navigation></main-navigation>

        <div class="dashboard-header">

            <div class="dashboard-header__new" v-click-outside="() => {this.openGroupListDropdown = false}">
                <button class="btn btn-primary" type="button" @click="openGroupListDropdown = !openGroupListDropdown">
                    <md-icon>edit</md-icon>
                    {{ $t('product_admin.page.list.button.groups') }}
                </button>
                <product-type-dropdown
                    :open="openGroupListDropdown"
                    :productTypes="productGroups"
                    @selectedType="selectedProductType">
                </product-type-dropdown>
            </div>

            <div class="dashboard-header__filters">
                <div class="product-list-page__header-search-flex">
                    <div class="search-container">
                        <search :searchData="searchData" @searchPhrase="(value) => {this.searchPhrase = value}"></search>
                    </div>
                </div>

                <div class="product-list-page__header-controls">
                    <button type="button" @click="refresh()">
                        <md-icon>refresh</md-icon>
                    </button>
                </div>
            </div>
        </div>

        <div class="filter-column">
            <group-admin-pages selected-page="GroupCategoryProductTypeList" :group="group"></group-admin-pages>
            <basic-links position="bottom"></basic-links>
        </div>

        <div class="list-column">
            <vue-good-table
                :columns="tableColumns"
                :rows="getProductRows()"
                :sort-options="{enabled: false}"
                styleClass="vgt-table non-interactive"
                @on-row-click="onRowClick"
                @on-row-mouseleave="onRowMouseleave">
                <template slot="table-row" slot-scope="props">
                    <div class="col-name" v-if="props.column.field === 'name'">
                        <div :style="`margin-left:${props.row.depth * 20}px`">{{ props.row.name }}</div>
                    </div>
                    <span v-else-if="props.column.field === 'type'">{{ getTypeName(props.formattedRow[props.column.field]) }}</span>
                    <div v-else-if="Array.isArray(props.formattedRow[props.column.field])">
                        <div v-for="(value, index) in props.formattedRow[props.column.field]" :key="index">{{ value }}</div>
                    </div>
                    <div class="col-options" v-else-if="props.column.field === 'options' && props.row.actions.length > 0">
                        <button @click="props.row.showDropdown = !props.row.showDropdown" class="element-options">
                            <md-icon class="element-options">more_vert</md-icon>
                        </button>

                        <dropdown
                            @clicked="onDropdownClick($event, props.page)"
                            :open="props.row.showDropdown"
                            :options="props.row.actions"
                            :elementId="props.row.id"
                            width="280"
                        ></dropdown>
                    </div>
                    <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                </template>
                <div slot="emptystate">{{ $t('group_category_product_type.page.list.text.empty_state') }}</div>
            </vue-good-table>
        </div>

        <modal-group-category-edit
            :open="openCategoryEditModal"
            :group-id="groupId"
            :category-id="selectedCategoryId"
            :create-target="selectedCreateTarget"
            @close="categoryEditEnd">
        </modal-group-category-edit>

        <modal-group-category-product-type-edit
            :open="openCategoryProductTypeEditModal"
            :group-id="groupId"
            :category-id="selectedCategoryId"
            :product-type-id="selectedCategoryProductTypeId"
            :create-target="selectedCreateTarget"
            @close="categoryProductTypeEditEnd">
        </modal-group-category-product-type-edit>

        <modal-confirmation
            :open="openConfirmationDeleteCategoryModal"
            :data="confirmationDeleteCategoryModalData"
            @accepted="categoryDelete"
            @close="openConfirmationDeleteCategoryModal = false"
        ></modal-confirmation>

        <modal-confirmation
            :open="openConfirmationDeleteCategoryProductTypeModal"
            :data="confirmationDeleteCategoryProductTypeModalData"
            @accepted="categoryProductTypeDelete"
            @close="openConfirmationDeleteCategoryProductTypeModal = false"
        ></modal-confirmation>

        <notification></notification>

        <div class="is-loading" v-show="loading"></div>
    </div>
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import {VueGoodTable} from 'vue-good-table'
import {MdIcon} from 'vue-material/dist/components'
import vClickOutside from 'v-click-outside'
import ProductTypeDropdown from '../components/dropdowns/ProductTypeDropdown.vue'

import MainNavigation from '../components/navigations/MainNavigation.vue'
import Dropdown from '../components/dropdowns/Dropdown.vue'
import Search from '../components/Search.vue'
import ModalGroupCategoryEdit from '../components/modals/ModalGroupCategoryEdit.vue'
import ModalGroupCategoryProductTypeEdit from '../components/modals/ModalGroupCategoryProductTypeEdit.vue'
import Notification from '../components/Notification.vue'
import ModalConfirmation from '../components/modals/ModalConfirmation'
import GroupAdminPages from "@/components/sidebars/GroupAdminPages";
import BasicLinks from '../components/BasicLinks.vue'

Vue.use(MdIcon)
Vue.use(VueGoodTable)

export default {
    name: 'GroupCategoryProductTypeList',

    data() {
        return {
            loading: true,
            selectedCategoryId: null,
            selectedCategoryProductTypeId: null,
            selectedCreateTarget: null,
            openCategoryEditModal: false,
            openCategoryProductTypeEditModal: false,
            openGroupListDropdown: false,
            productGroups: [],

            deleteCategoryId: null,
            openConfirmationDeleteCategoryModal: false,
            confirmationDeleteCategoryModalData: {
                acceptText: this.$t('group_category.confirmation.delete.ok'),
                cancelText: this.$t('group_category.confirmation.delete.cancel'),
                message: this.$t('group_category.confirmation.delete.question')
            },

            deleteCategoryProductTypeId: null,
            openConfirmationDeleteCategoryProductTypeModal: false,
            confirmationDeleteCategoryProductTypeModalData: {
                acceptText: this.$t('group_category_product_type.page.list.confirmation.delete.ok'),
                cancelText: this.$t('group_category_product_type.page.list.confirmation.delete.cancel'),
                message: this.$t('group_category_product_type.page.list.confirmation.delete.question')
            },

            searchData: {
                placeholder: this.$t('group_category_product_type.page.list.search.placeholder'),
                tooltipText: this.$t('group_category_product_type.page.list.search.tooltip'),
                countText: ''
            },

            searchPhrase: '',

            tableColumns: [
                {
                    label: this.$t('group_category_product_type.page.list.column.name'),
                    field: 'name',
                    width: '50%'
                },
                {
                    label: this.$t('group_category_product_type.page.list.column.type'),
                    field: 'type',
                    width: '44%'
                },
                {
                    label: '',
                    field: 'options',
                    width: '6%'
                }
            ]
        }
    },

    mounted() {
        this.$store.dispatch('FETCH_CURRENT_USER').then(() => {
            if (this.userHasProductAdmin) {
                this.$store.dispatch('FETCH_PRODUCT_GROUPS').then(data => {
                    this.productGroups = data;
                    this.fetchData()
                })
            } else {
                this.$router.push('/')
            }
        }).catch((redirect) => {
            redirect && this.$router.push('/');
        })
    },

    watch: {
        '$route'() {
            this.$store.commit('resetGroupCategoryProductTypeList')
            this.refresh()
        }
    },

    methods: {
        getProductRows() {
            let tableData = this.groupCategoryProductTypeRows, searchPhrase

            if (this.searchPhrase !== '') {
                searchPhrase = this.searchPhrase.toLowerCase().split(/\s+/)
                tableData = tableData.filter(row => {
                    let valid = true
                    for (let value of searchPhrase) {
                        valid = valid && row.search.toLowerCase().indexOf(value) > -1
                    }
                    return valid
                })
            }

            this.searchData.countText = this.$tc('group_category_product_type.page.list.search.name', tableData.length)

            return tableData
        },

        onDropdownClick(val) {
            switch (val.action.action) {
            case 'category-edit':
                this.selectedCategoryId = val.id
                this.selectedCreateTarget = null
                this.openCategoryEditModal = true
                break
            case 'category-create':
                this.selectedCategoryId = val.id
                this.selectedCreateTarget = val.action.target
                this.openCategoryEditModal = true
                break
            case 'category-delete':
                this.deleteCategoryId = val.id
                this.openConfirmationDeleteCategoryModal = true
                break
            case 'category-product-type-create':
                this.selectedCategoryId = val.id
                this.selectedCategoryProductTypeId = null
                this.selectedCreateTarget = val.action.target
                this.openCategoryProductTypeEditModal = true
                break
            case 'product-type-edit':
                this.selectedCategoryId = val.action.categoryId
                this.selectedCategoryProductTypeId = val.id
                this.selectedCreateTarget = null
                this.openCategoryProductTypeEditModal = true
                break
            case 'product-type-create':
                this.selectedCategoryId = val.action.categoryId
                this.selectedCategoryProductTypeId = val.id
                this.selectedCreateTarget = val.action.target
                this.openCategoryProductTypeEditModal = true
                break
            case 'product-type-delete':
                this.deleteCategoryId = val.action.categoryId
                this.deleteCategoryProductTypeId = val.id
                this.openConfirmationDeleteCategoryProductTypeModal = true
                break
            }
        },

        getTypeName(type) {
            switch (type) {
            case 'category': return 'Kategória';
            case 'product_type': return 'Terméktípus';
            default: return type;
            }
        },

        fetchData() {
            this.$store.dispatch('FETCH_GROUP_CATEGORY_PRODUCT_TYPES', this.$route.params.groupId).then(data => {
                this.loading = false;
            })
        },

        categoryEditEnd(refresh) {
            this.openCategoryEditModal = false
            if (refresh) {
                this.refresh()
            }
        },

        categoryDelete() {
            let payload = {
                groupId: this.$route.params.groupId,
                categoryId: this.deleteCategoryId
            }

            this.$store.dispatch('DELETE_GROUP_CATEGORY', payload).then(() => {
                this.$store.commit('setNotification', [this.$t('group_category.confirmation.delete.notification.success')])
                this.deleteCategoryId = null
                this.refresh()
            }).catch(() => {
                this.$store.commit('setNotification', [this.$t('group_category.confirmation.delete.notification.failed')])
            }).finally(() => {
                this.openConfirmationDeleteCategoryModal = false
            })
        },

        categoryProductTypeEditEnd(refresh) {
            this.openCategoryProductTypeEditModal = false
            if (refresh) {
                this.refresh()
            }
        },

        categoryProductTypeDelete() {
            let payload = {
                groupId: this.$route.params.groupId,
                categoryId: this.deleteCategoryId,
                productTypeId: this.deleteCategoryProductTypeId
            }

            this.$store.dispatch('DELETE_GROUP_CATEGORY_PRODUCT_TYPE', payload).then(() => {
                this.$store.commit('setNotification', [this.$t('group_category_product_type.page.list.confirmation.delete.notification.success')])
                this.deleteCategoryId = null
                this.deleteCategoryProductTypeId = null
                this.refresh()
            }).catch(() => {
                this.$store.commit('setNotification', [this.$t('group_category_product_type.page.list.confirmation.delete.notification.failed')])
            }).finally(() => {
                this.openConfirmationDeleteCategoryProductTypeModal = false
            })
        },

        onRowClick(params) {
            if (!params.event.target.className.includes('element-options')) {
                params.row.showDropdown = false
            }
        },

        onRowMouseleave(el) {
            el.row.showDropdown = false
        },

        selectedProductType(payload) {
            this.openCategoryEditModal = false
            this.openGroupListDropdown = false
            this.$router.push({name: 'GroupAdmin', params: {groupId: payload.id}})
        },

        refresh() {
            this.loading = true
            this.fetchData()
        },
    },

    directives: {
        clickOutside: vClickOutside.directive
    },

    computed: {
        groupId() {
            return this.$route.params.groupId
        },

        group() {
            return this.productGroups.find(group => group.id === Number(this.groupId))
        },

        ...mapGetters(['groupCategoryProductTypeRows', 'groupTypeCategories', 'userHasProductAdmin'])
    },

    components: {
        MainNavigation,
        VueGoodTable,
        ProductTypeDropdown,
        Search,
        Dropdown,
        ModalGroupCategoryEdit,
        ModalGroupCategoryProductTypeEdit,
        Notification,
        BasicLinks,
        GroupAdminPages,
        ModalConfirmation
    }
}
</script>

<style lang="scss">
@import '../assets/sass/ui/tooltip.scss';
@import '../assets/sass/ui/vuetable.scss';
@import '../assets/sass/ui/loading.scss';
</style>

<style lang="scss" scoped>
@import '../assets/sass/toolkit.scss';
@import '../assets/sass/ui/input.scss';
@import '../assets/sass/ui/button.scss';
@import '../assets/sass/partials/dashboard-header.scss';
@import '../assets/sass/layout/list-page.scss';

.product-list-page {
    .dashboard-header {
        .search-container {
            width: 100%;
            max-width: 640px;
        }

        .checkbox {
            margin-left: auto;
            margin-right: 32px;
        }

        .filter-component {
            margin-left: 15px;
        }
    }

    &__header-search-flex {
        display: flex;
        align-items: center;
        flex-basis: calc(100% - 70px);
    }

    &__header-controls {
        padding-right: 30px;
    }

    .list-column {
        font-weight: 500;
    }

    .filter-column {
        max-width: calc(100vh - 140px);
        overflow-x: hidden;
        overflow-y: auto;
    }
}
</style>
